// Auftragsverarbeitungsvertrag (AVV) Template Data
// Based on Art. 28 Abs. 3 DSGVO

export interface AVVContractParty {
  name: string;
  address: string;
  representedBy: string;
  dataProtectionOfficer: string;
  dpoEmail: string;
  dpoPhone: string;
}

export interface AVVProjectDetails {
  projectName: string;
  projectDescription: string;
  contractStart: string;
  contractEnd: string;
  dataSubjectCategories: string[];
  personalDataCategories: string[];
  processingPurposes: string[];
  mainContractReference: string;
}

export interface SubProcessor {
  name: string;
  function: string;
  location: string;
  legalBasis: string;
  dataProcessed: string;
}

export interface TOMCategory {
  title: string;
  measures: string[];
}

export interface AVVClause {
  id: number;
  title: string;
  legalBasis: string;
  content: string;
}

export interface AVVAnnex {
  id: number;
  title: string;
  content: string;
}

export interface AVVTemplateData {
  processor: AVVContractParty;
  defaultController: AVVContractParty;
  defaultProject: AVVProjectDetails;
  clauses: AVVClause[];
  subProcessors: SubProcessor[];
  toms: TOMCategory[];
  annexes: AVVAnnex[];
}

export const avvTemplateData: AVVTemplateData = {
  processor: {
    name: 'PaperVerse UG (haftungsbeschränkt)',
    address: 'Moritzburger Straße 39, 01127 Dresden',
    representedBy: 'Oliver Mey, Geschäftsführer',
    dataProtectionOfficer: 'Oliver Mey',
    dpoEmail: 'contact@paperverse.io',
    dpoPhone: '[Telefonnummer]',
  },

  defaultController: {
    name: 'Fraunhofer-Institut für Elektronische Nanosysteme ENAS',
    address: 'Technologie-Campus 3, 09126 Chemnitz',
    representedBy: '[Vertretungsberechtigte Person]',
    dataProtectionOfficer: '[Name des DSB Fraunhofer]',
    dpoEmail: '[datenschutz@enas.fraunhofer.de]',
    dpoPhone: '[Telefonnummer]',
  },

  defaultProject: {
    projectName: 'Pilotprojekt wissenschaftliche Publikationsplattform',
    projectDescription:
      'Hosting und Verwaltung wissenschaftlicher Publikationen auf der PaperVerse-Plattform, einschließlich KI-gestützter Analyse, Peer-Review-Management und Suchmaschinenindexierung.',
    contractStart: '',
    contractEnd: '',
    dataSubjectCategories: [
      'Forscher und wissenschaftliche Mitarbeiter des Auftraggebers',
      'Autoren wissenschaftlicher Publikationen',
      'Reviewer und Gutachter',
      'Registrierte Nutzer der Plattform',
    ],
    personalDataCategories: [
      'Kontaktdaten (Name, Institution, ORCID-ID)',
      'Authentifizierungsdaten (E-Mail-Adresse, gehashtes Passwort)',
      'Inhaltsdaten (Papers, Kommentare, Reviews)',
      'Nutzungsdaten (Seitenaufrufe, Klickverhalten, Session-IDs)',
      'Geräteinformationen (Browser, Betriebssystem, Sprache)',
      'Zahlungsdaten (über Paddle als Sub-Processor, keine Kreditkartendaten bei PaperVerse)',
      'Einwilligungsdaten (Consent-Protokollierung mit IP-Adresse und User-Agent)',
    ],
    processingPurposes: [
      'Bereitstellung und Betrieb der Publikationsplattform',
      'Speicherung und Verwaltung wissenschaftlicher Arbeiten',
      'Durchführung KI-gestützter Reviews (Google Vertex AI)',
      'Verwaltung von Kommentaren und Peer-Reviews',
      'Nutzerregistrierung und Authentifizierung',
      'Web-Analytics zur Plattformoptimierung',
      'Suchmaschinenindexierung öffentlicher Publikationen',
      'Zahlungsabwicklung für Premium-Funktionen',
    ],
    mainContractReference: '',
  },

  clauses: [
    {
      id: 1,
      title: '§ 1 Gegenstand, Dauer, Art und Zweck der Verarbeitung',
      legalBasis: 'Art. 28 Abs. 3 S. 1 DSGVO',
      content: `(1) Dieser Auftragsverarbeitungsvertrag konkretisiert die datenschutzrechtlichen Pflichten der Parteien aus dem zwischen ihnen geschlossenen Hauptvertrag (nachfolgend „Hauptvertrag"). {{HAUPTVERTRAG_REFERENZ}}

Der Auftragnehmer verarbeitet personenbezogene Daten im Auftrag des Auftraggebers im Rahmen des oben beschriebenen Projekts. Gegenstand der Auftragsverarbeitung ist die Bereitstellung der PaperVerse-Plattform als Software-as-a-Service (SaaS) für die Verwaltung und Veröffentlichung wissenschaftlicher Publikationen.

(2) Die Dauer der Verarbeitung entspricht der Laufzeit des Vertrags. Sie beginnt mit dem im Vertragskopf genannten Datum und endet mit Ablauf der vereinbarten Vertragslaufzeit oder durch Kündigung.

(3) Art und Zweck der Verarbeitung umfassen:
- Speicherung und Verwaltung wissenschaftlicher Arbeiten und zugehöriger Metadaten
- Bereitstellung von Nutzerkonten mit Authentifizierung und Autorisierung
- Durchführung KI-gestützter Analysen und Reviews mittels Google Vertex AI
- Verwaltung von Kommentaren, Bewertungen und Peer-Reviews
- Erhebung und Auswertung anonymisierter Nutzungsstatistiken
- Indexierung öffentlicher Publikationen bei Suchmaschinen (Google, Bing)
- Abwicklung von Zahlungsvorgängen über Paddle`,
    },
    {
      id: 2,
      title: '§ 2 Art der personenbezogenen Daten und Kategorien betroffener Personen',
      legalBasis: 'Art. 28 Abs. 3 S. 1 DSGVO',
      content: `(1) Folgende Arten personenbezogener Daten sind Gegenstand der Verarbeitung:
- Kontaktdaten: Name, institutionelle Zugehörigkeit, ORCID-ID
- Authentifizierungsdaten: E-Mail-Adresse, gehashte Passwörter (über den eingesetzten Authentifizierungsdienst)
- Inhaltsdaten: Wissenschaftliche Arbeiten (Papers), Kommentare, Reviews
- Nutzungsdaten: Seitenaufrufe, Klickverhalten, Session-Informationen
- Gerätedaten: Browsertyp, Betriebssystem, Spracheinstellung
- Einwilligungsdaten: Consent-Protokolle inkl. IP-Adresse und User-Agent
- Zahlungsdaten: Transaktions-IDs und Abrechnungsinformationen (via Paddle)

(2) Kategorien betroffener Personen:
- Forscher und wissenschaftliche Mitarbeiter des Auftraggebers
- Autoren wissenschaftlicher Publikationen
- Reviewer und Gutachter
- Registrierte Nutzer der Plattform, die dem Auftraggeber zuzuordnen sind`,
    },
    {
      id: 3,
      title: '§ 3 Weisungsgebundenheit',
      legalBasis: 'Art. 28 Abs. 3 S. 2 lit. a DSGVO',
      content: `(1) Der Auftragnehmer verarbeitet personenbezogene Daten ausschließlich auf dokumentierte Weisung des Auftraggebers, es sei denn, er ist nach dem Recht der Union oder der Mitgliedstaaten, dem er unterliegt, zur Verarbeitung verpflichtet. In einem solchen Fall teilt der Auftragnehmer dem Auftraggeber diese rechtlichen Anforderungen vor der Verarbeitung mit, sofern das betreffende Recht dies nicht wegen eines wichtigen öffentlichen Interesses verbietet.

(2) Der Auftragnehmer informiert den Auftraggeber unverzüglich, wenn er der Auffassung ist, dass eine Weisung gegen datenschutzrechtliche Vorschriften verstößt. Der Auftragnehmer ist berechtigt, die Durchführung der entsprechenden Weisung solange auszusetzen, bis sie durch den Auftraggeber bestätigt oder geändert wird.

(3) Weisungen werden in der Regel schriftlich oder in Textform (E-Mail) erteilt. Mündliche Weisungen sind unverzüglich schriftlich zu bestätigen. Das Weisungsprotokoll (Anlage 4) dokumentiert alle erteilten Weisungen.`,
    },
    {
      id: 4,
      title: '§ 4 Vertraulichkeit',
      legalBasis: 'Art. 28 Abs. 3 S. 2 lit. b DSGVO',
      content: `(1) Der Auftragnehmer gewährleistet, dass sich die zur Verarbeitung der personenbezogenen Daten befugten Personen zur Vertraulichkeit verpflichtet haben oder einer angemessenen gesetzlichen Verschwiegenheitspflicht unterliegen.

(2) Die Vertraulichkeitspflicht besteht auch nach Beendigung des Auftrags fort.

(3) Der Auftragnehmer stellt sicher, dass der Zugang zu personenbezogenen Daten auf diejenigen Personen beschränkt ist, die diesen Zugang für die Erfüllung des Auftrags benötigen (Need-to-know-Prinzip).`,
    },
    {
      id: 5,
      title: '§ 5 Technische und organisatorische Maßnahmen',
      legalBasis: 'Art. 28 Abs. 3 S. 2 lit. c, Art. 32 DSGVO',
      content: `(1) Der Auftragnehmer ergreift alle gemäß Art. 32 DSGVO erforderlichen technischen und organisatorischen Maßnahmen, um ein dem Risiko angemessenes Schutzniveau zu gewährleisten. Die Maßnahmen sind in Anlage 1 (Technische und organisatorische Maßnahmen) im Detail beschrieben.

(2) Der Auftragnehmer überprüft die Wirksamkeit der technischen und organisatorischen Maßnahmen regelmäßig und passt diese bei Bedarf dem aktuellen Stand der Technik an.

(3) Die wesentlichen Maßnahmen umfassen unter anderem:
- Zugriffskontrolle auf Datenbankebene mit Mandantentrennung
- Token-basierte Authentifizierung
- Rollenbasierte Zugriffskontrolle
- TLS/HTTPS-Verschlüsselung aller Datenübertragungen
- Verschlüsselung ruhender Daten (at-rest encryption)
- Rate-Limiting zum Schutz vor Missbrauch
- Anonymisierung von Nutzungsdaten bei Account-Löschung`,
    },
    {
      id: 6,
      title: '§ 6 Unterauftragsverarbeitung',
      legalBasis: 'Art. 28 Abs. 2, Abs. 3 S. 2 lit. d, Abs. 4 DSGVO',
      content: `(1) Der Auftragnehmer setzt die in Anlage 2 aufgeführten Unterauftragsverarbeiter ein. Der Auftraggeber erteilt hiermit seine allgemeine schriftliche Genehmigung gemäß Art. 28 Abs. 2 DSGVO für den Einsatz weiterer Unterauftragsverarbeiter.

(2) Der Auftragnehmer informiert den Auftraggeber über jede beabsichtigte Änderung in Bezug auf die Hinzuziehung oder Ersetzung von Unterauftragsverarbeitern. Der Auftraggeber kann gegen derartige Änderungen innerhalb von 14 Tagen nach Mitteilung Einspruch erheben.

(3) Bei der Einschaltung von Unterauftragsverarbeitern stellt der Auftragnehmer sicher, dass diesen dieselben Datenschutzpflichten auferlegt werden, wie sie in diesem Vertrag festgelegt sind, insbesondere hinreichende Garantien dafür, dass die geeigneten technischen und organisatorischen Maßnahmen so durchgeführt werden, dass die Verarbeitung entsprechend den Anforderungen der DSGVO erfolgt.

(4) Die aktuelle Liste der Unterauftragsverarbeiter ist in Anlage 2 beigefügt.`,
    },
    {
      id: 7,
      title: '§ 7 Unterstützung bei Betroffenenrechten',
      legalBasis: 'Art. 28 Abs. 3 S. 2 lit. e DSGVO',
      content: `(1) Der Auftragnehmer unterstützt den Auftraggeber nach Möglichkeit mit geeigneten technischen und organisatorischen Maßnahmen bei der Erfüllung der Pflicht zur Beantwortung von Anträgen auf Wahrnehmung der Betroffenenrechte gemäß Art. 15 bis 22 DSGVO.

(2) Der Auftragnehmer hat technische Maßnahmen implementiert, die insbesondere folgende Betroffenenrechte unterstützen:
- Vollständiger Export aller personenbezogenen Daten (Art. 15 DSGVO)
- Kaskadierende Löschung aller personenbezogenen Daten mit Anonymisierung verbleibender Nutzungsstatistiken (Art. 17 DSGVO)
- Einschränkung der Verarbeitung (Art. 18 DSGVO)
- Widerspruch gegen automatisierte Verarbeitung einschließlich KI-gestützter Analysen (Art. 21/22 DSGVO)

Die technischen Details der Umsetzung sind in Anlage 1 dokumentiert.

(3) Wendet sich ein Betroffener direkt an den Auftragnehmer, leitet dieser die Anfrage unverzüglich an den Auftraggeber weiter.`,
    },
    {
      id: 8,
      title: '§ 8 Meldepflichten bei Datenschutzverletzungen',
      legalBasis: 'Art. 28 Abs. 3 S. 2 lit. f, Art. 33–36 DSGVO',
      content: `(1) Der Auftragnehmer unterstützt den Auftraggeber bei der Einhaltung der in Art. 33 und 34 DSGVO genannten Pflichten (Meldung von Datenschutzverletzungen an die Aufsichtsbehörde und Benachrichtigung der betroffenen Personen).

(2) Der Auftragnehmer meldet dem Auftraggeber unverzüglich, spätestens jedoch innerhalb von 48 Stunden nach Kenntniserlangung, jede Verletzung des Schutzes personenbezogener Daten. Die Meldung enthält mindestens:
a) eine Beschreibung der Art der Verletzung,
b) die Kategorien und die ungefähre Anzahl der betroffenen Personen,
c) die Kategorien und die ungefähre Anzahl der betroffenen Datensätze,
d) eine Beschreibung der wahrscheinlichen Folgen,
e) eine Beschreibung der ergriffenen oder vorgeschlagenen Maßnahmen.

(3) Die Meldung erfolgt an den Datenschutzbeauftragten des Auftraggebers per E-Mail mit dem Betreff „DATENSCHUTZVORFALL – DRINGEND".

(4) Der Auftragnehmer unterstützt den Auftraggeber darüber hinaus bei der Durchführung von Datenschutz-Folgenabschätzungen gemäß Art. 35 DSGVO sowie bei der vorherigen Konsultation der Aufsichtsbehörde gemäß Art. 36 DSGVO, soweit die Verarbeitung im Rahmen dieses Vertrages betroffen ist. Der Auftragnehmer stellt dem Auftraggeber hierzu die erforderlichen Informationen zur Verfügung.`,
    },
    {
      id: 9,
      title: '§ 9 Löschung und Rückgabe von Daten',
      legalBasis: 'Art. 28 Abs. 3 S. 2 lit. g DSGVO',
      content: `(1) Nach Abschluss der Auftragsverarbeitung löscht der Auftragnehmer sämtliche personenbezogenen Daten, die im Auftrag des Auftraggebers verarbeitet wurden, oder gibt sie nach Wahl des Auftraggebers zurück, sofern nicht nach dem Unionsrecht oder dem Recht der Mitgliedstaaten eine Verpflichtung zur Speicherung besteht.

(2) Die Löschung erfolgt kaskadierend für alle personenbezogenen Daten, einschließlich Nutzerprofile, Inhaltsdaten, Kommentare, Reviews und Einwilligungsprotokolle. Nutzungsstatistiken werden anonymisiert, nicht vollständig gelöscht, um die Integrität aggregierter Analysedaten zu wahren.

(3) Der Auftragnehmer bestätigt die vollständige Löschung schriftlich. Die technische Umsetzung der Löschung ist in Anlage 1 dokumentiert.`,
    },
    {
      id: 10,
      title: '§ 10 Nachweispflichten und Kontrollen',
      legalBasis: 'Art. 28 Abs. 3 S. 2 lit. h DSGVO',
      content: `(1) Der Auftragnehmer stellt dem Auftraggeber alle erforderlichen Informationen zum Nachweis der Einhaltung der in Art. 28 DSGVO niedergelegten Pflichten zur Verfügung und ermöglicht und trägt zu Überprüfungen – einschließlich Inspektionen – bei, die vom Auftraggeber oder einem von diesem beauftragten Prüfer durchgeführt werden.

(2) Der Auftragnehmer führt Audit-Trails für Einwilligungen, Löschanfragen und Zugriffsbeschränkungen. Diese Nachweise werden dem Auftraggeber auf Anfrage zur Verfügung gestellt.

(3) Inspektionen und Audits werden nach angemessener Vorankündigung (mindestens 14 Tage) während der üblichen Geschäftszeiten durchgeführt. Die Kosten der Überprüfung trägt der Auftraggeber, es sei denn, die Überprüfung ergibt Verstöße des Auftragnehmers.`,
    },
    {
      id: 11,
      title: '§ 11 Drittlandtransfer',
      legalBasis: 'Art. 44–49 DSGVO',
      content: `(1) Die Hauptverarbeitung personenbezogener Daten (Datenbank, Authentifizierung, Storage, Edge Functions sowie KI-gestützte Analysen) erfolgt ausschließlich innerhalb der EU/des EWR (Supabase Inc.: AWS, Irland; Google Cloud Vertex AI: EU-Multi-Region auf Google-Cloud-Infrastruktur in EU/EWR-Rechenzentren) und stellt keinen Drittlandtransfer im Sinne der Art. 44 ff. DSGVO dar. Drittlandtransfers erfolgen ausschließlich für (a) die Frontend-Auslieferung über Lovable Labs (USA) – beschränkt auf technische Verbindungsdaten wie IP-Adresse und User-Agent – sowie (b) Hilfsdienste wie Suchmaschinen-Indexierung (Google LLC, Microsoft Corp.) und Zahlungsabwicklung (Paddle.com Market Ltd., UK/USA). Der Auftragnehmer stellt sicher, dass für sämtliche Übermittlungen personenbezogener Daten in Drittländer geeignete Garantien gemäß Art. 46 DSGVO bestehen.

(2) Folgende Garantien sind für die verbleibenden Drittlandtransfers implementiert:
a) **EU-US Data Privacy Framework (DPF)**: Google LLC (Indexing API) und Microsoft Corp. (Bing Webmaster API) sind unter dem EU-US Data Privacy Framework zertifiziert (Stand der Zertifizierung jeweils einsehbar unter https://www.dataprivacyframework.gov/list).
b) **EU-Standardvertragsklauseln (SCCs)**: Lovable Labs Inc. (Frontend-Hosting / CDN) ist nicht DPF-zertifiziert; die Übermittlung erfolgt auf Grundlage der von der EU-Kommission am 04.06.2021 genehmigten Standardvertragsklauseln (Durchführungsbeschluss (EU) 2021/914), Modul Zwei (Verantwortlicher–Auftragsverarbeiter). Ergänzend werden SCCs mit allen weiteren relevanten Sub-Processors mit Sitz außerhalb des EWR abgeschlossen. Diese sind in Anlage 3 beigefügt bzw. referenziert.
c) **UK Adequacy Decision**: Für Übermittlungen an Paddle.com Market Ltd. (UK) gilt der Angemessenheitsbeschluss der EU-Kommission für das Vereinigte Königreich.

(3) Datenstandorte der Sub-Processors:
- Supabase Inc. (Datenbank, Auth, Storage, Edge Functions): AWS, EU/EWR (Irland) – kein Drittlandtransfer
- Google Cloud / Vertex AI: EU-Multi-Region, ausschließlich EU/EWR-Rechenzentren – kein Drittlandtransfer
- Lovable Labs (Frontend-Hosting / CDN): San Francisco, CA, USA
- Google LLC (Indexing API): USA
- Microsoft / Bing API: USA
- Paddle (Zahlungen): UK/USA

(4) Der Auftragnehmer hat ein Transfer Impact Assessment (TIA) durchgeführt und stellt dieses dem Auftraggeber auf Anfrage zur Verfügung.

(5) Sollte eine der oben genannten Garantien unwirksam werden (z.B. durch Gerichtsentscheidung), informiert der Auftragnehmer den Auftraggeber unverzüglich und ergreift alternative Maßnahmen gemäß Art. 46 DSGVO.`,
    },
    {
      id: 12,
      title: '§ 12 Haftung',
      legalBasis: 'Art. 82 DSGVO',
      content: `(1) Die Haftung der Parteien richtet sich nach Art. 82 DSGVO in Verbindung mit den allgemeinen gesetzlichen Bestimmungen.

(2) Der Auftragnehmer haftet gegenüber dem Auftraggeber für Schäden, die durch eine nicht den Vorschriften der DSGVO entsprechende Verarbeitung oder durch eine Handlung entgegen den rechtmäßig erteilten Weisungen des Auftraggebers verursacht werden.

(3) Die Haftungsbeschränkungen des zugrunde liegenden Hauptvertrages gelten auch für diesen Auftragsverarbeitungsvertrag, soweit nicht zwingende gesetzliche Vorschriften entgegenstehen.`,
    },
    {
      id: 13,
      title: '§ 13 Laufzeit und Kündigung',
      legalBasis: 'Art. 28 Abs. 3 S. 1 DSGVO',
      content: `(1) Dieser Auftragsverarbeitungsvertrag wird auf die Dauer des Hauptvertrages geschlossen und endet automatisch mit dessen Beendigung.

(2) Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt. Ein wichtiger Grund liegt insbesondere vor, wenn:
a) der Auftragnehmer gegen wesentliche Bestimmungen dieses Vertrages verstößt,
b) der Auftragnehmer eine Weisung des Auftraggebers nicht befolgen kann oder will,
c) der Auftragnehmer den Zugang zu Überprüfungen gemäß § 10 verweigert,
d) nach einem Wechsel der Rechtslage die Verarbeitung in der bisherigen Form nicht mehr möglich ist.

(3) Nach Beendigung dieses Vertrages gelten die Bestimmungen in § 9 zur Löschung und Rückgabe von Daten.`,
    },
    {
      id: 14,
      title: '§ 14 Schlussbestimmungen',
      legalBasis: 'Allgemeines Vertragsrecht',
      content: `(1) Änderungen und Ergänzungen dieses Vertrages bedürfen der Schriftform. Dies gilt auch für die Aufhebung dieses Schriftformerfordernisses.

(2) Sollten einzelne Bestimmungen dieses Vertrages unwirksam sein oder werden, so wird die Wirksamkeit der übrigen Bestimmungen hiervon nicht berührt. Die unwirksame Bestimmung ist durch eine wirksame zu ersetzen, die dem wirtschaftlichen Zweck der unwirksamen Bestimmung am nächsten kommt.

(3) Es gilt das Recht der Bundesrepublik Deutschland.

(4) Gerichtsstand ist der Sitz des Auftragnehmers, soweit gesetzlich zulässig.`,
    },
  ],

  subProcessors: [
    {
      name: 'Supabase Inc. (AWS)',
      function: 'Datenbank, Authentifizierung, Storage, Edge Functions',
      location: 'AWS, EU/EWR (Irland)',
      legalBasis: 'EU/EWR – kein Drittlandtransfer',
      dataProcessed: 'Alle personenbezogenen Daten (Nutzerprofile, Papers, Kommentare, Reviews, Einwilligungen, Nutzungsstatistiken)',
    },
    {
      name: 'Google Cloud (Vertex AI)',
      function: 'KI-gestützte Reviews und Analysen wissenschaftlicher Arbeiten',
      location: 'EU-Multi-Region, ausschließlich EU/EWR-Rechenzentren',
      legalBasis: 'EU/EWR – kein Drittlandtransfer',
      dataProcessed: 'Paper-Inhalte (Titel, Abstracts, Volltexte) zur Analyse',
    },
    {
      name: 'Google Cloud Run (Tectonic-Satzdienst)',
      function: 'LaTeX-Kompilierung eingereichter Manuskripte (PaperVerse Writer)',
      location: 'EU/EWR (Google Cloud, EU-Region)',
      legalBasis: 'EU/EWR – kein Drittlandtransfer; Google Cloud DPA',
      dataProcessed: 'Manuskript-Quelltext (LaTeX), Bibliografie, Kompilierungsprotokolle',
    },
    {
      name: 'OurResearch (OpenAlex)',
      function: 'Offene bibliografische Datenbank für die Literatursuche',
      location: 'USA',
      legalBasis: 'Kein Auftragsverarbeitungsverhältnis – es werden ausschließlich thematische Suchbegriffe ohne Personenbezug übermittelt',
      dataProcessed: 'Thematischer Suchbegriff (bereinigt, ohne Identität, ohne Manuskriptinhalt)',
    },
    {
      name: 'Lovable Labs Inc.',
      function: 'Frontend-Hosting / CDN (Auslieferung statischer Assets)',
      location: 'San Francisco, CA, USA',
      legalBasis: 'EU-Standardvertragsklauseln (SCCs) – Modul Zwei (Durchführungsbeschluss (EU) 2021/914)',
      dataProcessed: 'Technische Verbindungsdaten (IP-Adresse, User-Agent, Server-Logs)',
    },
    {
      name: 'Google LLC (Search Indexing API)',
      function: 'Einreichung öffentlicher Paper-URLs bei Google Search',
      location: 'USA',
      legalBasis: 'EU-US Data Privacy Framework',
      dataProcessed: 'Öffentliche Paper-URLs (keine personenbezogenen Daten im engeren Sinne)',
    },
    {
      name: 'Microsoft Corp. (Bing Webmaster API)',
      function: 'Einreichung öffentlicher Paper-URLs bei Bing',
      location: 'USA',
      legalBasis: 'EU-US Data Privacy Framework',
      dataProcessed: 'Öffentliche Paper-URLs (keine personenbezogenen Daten im engeren Sinne)',
    },
    {
      name: 'Paddle.com Market Ltd.',
      function: 'Zahlungsabwicklung für Premium-Funktionen (Merchant of Record)',
      location: 'UK / USA',
      legalBasis: 'UK Adequacy Decision / SCCs',
      dataProcessed: 'User-IDs, Transaktionsdaten, Zahlungsinformationen',
    },
    {
      name: 'Resend, Inc.',
      function: 'Versand transaktionaler E-Mails (z. B. Bestätigungen, Benachrichtigungen)',
      location: 'USA',
      legalBasis: 'SCCs (EU-Standardvertragsklauseln)',
      dataProcessed: 'E-Mail-Adressen, Empfängername, E-Mail-Inhalt',
    },
  ],

  toms: [
    {
      title: 'Zutrittskontrolle',
      measures: [
        'Hosting bei AWS (Supabase) mit physischen Sicherheitsmaßnahmen nach ISO 27001',
        'Kein physischer Zugang zu Servern durch den Auftragnehmer erforderlich (Cloud-basiert)',
      ],
    },
    {
      title: 'Zugangskontrolle',
      measures: [
        'Token-basierte Authentifizierung mit branchenüblichem Verfahren',
        'Sichere Passwortspeicherung mittels kryptographischem Hashing',
        'Rate-Limiting auf API-Ebene zur Abwehr von Brute-Force-Angriffen',
        'Serverseitige Funktionen mit automatischer Token-Verifikation',
      ],
    },
    {
      title: 'Zugriffskontrolle',
      measures: [
        'Row-Level Security (RLS) auf allen Datenbanktabellen mit differenzierten Policies',
        'Rollenbasierte Zugriffskontrolle (RBAC) mit den Rollen „admin" und „user"',
        'Need-to-know-Prinzip: Nutzer sehen nur eigene Daten (durch RLS erzwungen)',
        'Separate Policies für SELECT, INSERT, UPDATE und DELETE je Tabelle',
      ],
    },
    {
      title: 'Weitergabekontrolle / Transportverschlüsselung',
      measures: [
        'TLS/HTTPS für alle Datenübertragungen (erzwungen durch Supabase und Hosting)',
        'Verschlüsselte Verbindungen zu allen Sub-Processors',
        'Keine unverschlüsselte Datenübertragung möglich',
      ],
    },
    {
      title: 'Eingabekontrolle',
      measures: [
        'Consent-Audit-Trail mit Zeitstempel, IP-Adresse, User-Agent und Dokumentenversion',
        'Lösch-Audit-Trail mit Status und gelöschten Datenkategorien',
        'Nachvollziehbare Datenbankoperationen durch automatische Zeitstempel auf allen Datensätzen',
      ],
    },
    {
      title: 'Auftragskontrolle',
      measures: [
        'Dieser Auftragsverarbeitungsvertrag mit dokumentierter Weisungsbindung',
        'Weisungsprotokoll (Anlage 4) für alle Weisungen des Auftraggebers',
        'Regelmäßige Überprüfung der Sub-Processor-Verträge',
      ],
    },
    {
      title: 'Verfügbarkeitskontrolle',
      measures: [
        'Supabase (AWS) mit integrierter Hochverfügbarkeit und automatischen Backups',
        'Point-in-Time Recovery für Datenbankwiederherstellung',
        'Redundante Speicherung durch AWS-Infrastruktur',
      ],
    },
    {
      title: 'Trennungsgebot',
      measures: [
        'Logische Datentrennung durch Mandantentrennung auf Datenbankebene',
        'Row-Level Security stellt sicher, dass Nutzer nur auf eigene Daten zugreifen',
        'Separate Datenhaltung für verschiedene Datenarten (Nutzerprofile, Publikationen, Kommentare, Reviews)',
      ],
    },
    {
      title: 'Datensparsamkeit und Pseudonymisierung',
      measures: [
        'Anonymes Tracking (Tier 1) ohne persistente Identifikatoren möglich',
        'TDDDG § 25 konform: kein localStorage/sessionStorage-Zugriff ohne Consent',
        'Anonymisierung von Nutzungsstatistiken bei Account-Löschung',
        'Widerspruchsmöglichkeit gegen KI-Verarbeitung über Nutzerpräferenzen',
        'Widerspruchsmöglichkeit gegen Analytics über Nutzerpräferenzen',
      ],
    },
    {
      title: 'Betroffenenrechte – technische Umsetzung',
      measures: [
        'Datenexport: Automatisierter Datenexport aller personenbezogenen Daten im maschinenlesbaren Format (JSON) (Art. 15/20 DSGVO)',
        'Datenlöschung: Kaskadierende Löschung aller personenbezogenen Daten (Profil, Dokumente, Kommentare, Reviews, Einwilligungen, Abonnements) mit Anonymisierung von Nutzungsstatistiken (Art. 17 DSGVO)',
        'Einschränkung der Verarbeitung: Technisches Flag in Nutzerpräferenzen ermöglicht die sofortige Einschränkung der Verarbeitung (Art. 18 DSGVO)',
        'Widerspruch gegen KI-Verarbeitung: Technisches Flag in Nutzerpräferenzen unterbindet automatisierte Analysen (Art. 21/22 DSGVO)',
        'Consent-Audit-Trail: Dedizierter Audit-Trail protokolliert alle Einwilligungen mit Zeitstempel, IP-Adresse, User-Agent und Dokumentenversion',
        'Lösch-Audit-Trail: Dedizierter Audit-Trail dokumentiert alle Löschanfragen mit Status und gelöschten Datenkategorien',
        'Rate-Limiting: Automatisiertes Rate-Limiting dokumentiert und erzwingt Zugriffsbeschränkungen',
      ],
    },
  ],

  annexes: [
    {
      id: 1,
      title: 'Anlage 1: Technische und organisatorische Maßnahmen (TOMs)',
      content: 'Die nachfolgend aufgeführten technischen und organisatorischen Maßnahmen gemäß Art. 32 DSGVO werden vom Auftragnehmer zum Zeitpunkt des Vertragsschlusses umgesetzt. Sie werden regelmäßig überprüft und dem Stand der Technik angepasst. Änderungen, die das Schutzniveau nicht unterschreiten, sind ohne Zustimmung des Auftraggebers zulässig.',
    },
    {
      id: 2,
      title: 'Anlage 2: Liste der Unterauftragsverarbeiter',
      content: 'Folgende Unterauftragsverarbeiter werden zum Zeitpunkt des Vertragsschlusses eingesetzt. Änderungen werden dem Auftraggeber gemäß § 6 Abs. 2 dieses Vertrags mitgeteilt. Der Auftraggeber kann innerhalb von 14 Tagen nach Mitteilung Einspruch erheben.',
    },
    {
      id: 3,
      title: 'Anlage 3: EU-Standardvertragsklauseln (SCCs)',
      content: `Für die Übermittlung personenbezogener Daten in Drittländer gelten die EU-Standardvertragsklauseln gemäß Durchführungsbeschluss (EU) 2021/914 der Kommission vom 4. Juni 2021.

Die SCCs werden in der jeweils gültigen Fassung als Bestandteil dieses Vertrages vereinbart. Sie gelten für alle in Anlage 2 genannten Unterauftragsverarbeiter mit Sitz außerhalb des EWR.

Modul Zwei (Controller-to-Processor) findet Anwendung.

Die SCCs können unter folgendem Link eingesehen werden:
https://eur-lex.europa.eu/eli/dec_impl/2021/914/oj

Hinweis: Für Supabase Inc. (Hauptdatenverarbeitung – Datenbank, Auth, Storage, Edge Functions) sind die SCCs nicht erforderlich, da die Verarbeitung ausschließlich auf AWS-Infrastruktur innerhalb der EU/des EWR (Irland) stattfindet und somit kein Drittlandtransfer im Sinne der Art. 44 ff. DSGVO vorliegt. Ebenso erfolgt die KI-gestützte Verarbeitung über Google Cloud Vertex AI ausschließlich in der EU-Multi-Region auf EU/EWR-Rechenzentren.

Für die verbleibenden Sub-Processors mit Sitz in den USA gilt: Google LLC (Indexing API) und Microsoft Corp. (Bing Webmaster API) sind unter dem EU-US Data Privacy Framework (DPF) aktiv zertifiziert; ihre Verarbeitung beschränkt sich auf den öffentlichen Sitemap-/Index-URL und enthält keine personenbezogenen Daten der Endnutzer. Lovable Labs Inc. (548 Market St, San Francisco, CA 94104, USA) stellt ausschließlich das statische Plattform-Hosting der Web-Anwendung bereit und verarbeitet keine Backend-Datenbankinhalte; für diese Übermittlung gelten die EU-Standardvertragsklauseln (Modul Zwei) gemäß Durchführungsbeschluss (EU) 2021/914 als integraler Bestandteil dieses AVV.

Die Anhänge I und II der Standardvertragsklauseln (Angaben zu den Parteien, Beschreibung der Übermittlung und technische/organisatorische Maßnahmen) werden durch die entsprechenden Angaben in diesem AVV (Vertragskopf, § 1–2 sowie Anlage 1) ausgefüllt.`,
    },
    {
      id: 4,
      title: 'Anlage 4: Weisungsprotokoll',
      content: `Dieses Protokoll dokumentiert alle Weisungen des Auftraggebers an den Auftragnehmer gemäß § 3 dieses Vertrages.

Weisungen sind schriftlich oder per E-Mail zu erteilen und werden in diesem Protokoll dokumentiert.`,
    },
  ],
};

// Column headers for the Weisungsprotokoll table (Anlage 4)
export const weisungsprotokollColumns = [
  'Nr.', 'Datum', 'Weisung', 'Erteilt von',
  'Empfangen von', 'Umgesetzt am', 'Bemerkungen',
];

// Helper to generate a complete AVV document title
export const generateAVVTitle = (controllerName: string): string => {
  return `Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO zwischen ${controllerName} und PaperVerse UG (haftungsbeschränkt)`;
};

// Helper to replace the main contract reference placeholder in clause content
export const replaceMainContractPlaceholder = (content: string, reference: string): string => {
  const replacement = reference?.trim()
    ? `Der Hauptvertrag im Sinne dieses AVV ist: ${reference}.`
    : '[Hauptvertrag hier eintragen]';
  return content.replace('{{HAUPTVERTRAG_REFERENZ}}', replacement);
};

// Saved AVV interface for localStorage persistence
export interface SavedAVV {
  id: string;
  controller: AVVContractParty;
  project: AVVProjectDetails;
  createdAt: string;
  updatedAt: string;
  status: 'draft' | 'finalized';
}

// LocalStorage key
export const AVV_STORAGE_KEY = 'paperverse_avv_contracts';

// Completeness check: returns list of missing field labels
export const getIncompleteAVVFields = (avv: SavedAVV): string[] => {
  const missing: string[] = [];
  if (!avv.controller.name?.trim()) missing.push('Organisation');
  if (!avv.controller.address?.trim()) missing.push('Anschrift');
  if (!avv.project.projectName?.trim()) missing.push('Projektbezeichnung');
  if (!avv.controller.dataProtectionOfficer?.trim()) missing.push('DSB-Name');
  if (!avv.controller.dpoEmail?.trim()) missing.push('DSB-E-Mail');
  if (!avv.project.contractStart?.trim()) missing.push('Vertragsbeginn');
  if (!avv.project.contractEnd?.trim()) missing.push('Vertragsende');
  if (!avv.project.dataSubjectCategories?.length) missing.push('Betroffenenkategorien');
  if (!avv.project.personalDataCategories?.length) missing.push('Datenkategorien');
  if (!avv.project.processingPurposes?.length) missing.push('Verarbeitungszwecke');
  if (!avv.project.mainContractReference?.trim()) missing.push('Hauptvertrag-Referenz');
  return missing;
};
